import { formatDateWithWeekday, getISODate } from '@/lib/dates'
import { type DayGroup, detectOverlaps, sumWorkHours } from '@/lib/group-entries-by-date'
import type { TimeEntriesExpanded } from '@/types/timeTracking'

export interface DayUserGroup {
    userId: string
    userName: string
    userColor?: string | null
    dayGroup: DayGroup
}

export interface DaySection {
    dateKey: string
    dateLabel: string
    totalWorkHours: number
    pendingCount: number
    data: DayUserGroup[]
}

export function groupEntriesByDayThenUser(
    entriesByUser: Record<string, TimeEntriesExpanded[]>,
    userColorMap?: Map<string, string>
): DaySection[] {
    const dayMap = new Map<string, Map<string, TimeEntriesExpanded[]>>()

    for (const [userId, entries] of Object.entries(entriesByUser)) {
        for (const entry of entries) {
            const dateKey = getISODate(entry.clock_in)
            let userMap = dayMap.get(dateKey)
            if (!userMap) {
                userMap = new Map()
                dayMap.set(dateKey, userMap)
            }
            let userEntries = userMap.get(userId)
            if (!userEntries) {
                userEntries = []
                userMap.set(userId, userEntries)
            }
            userEntries.push(entry)
        }
    }

    const sections: DaySection[] = []

    for (const [dateKey, userMap] of dayMap) {
        const dayUserGroups: DayUserGroup[] = []
        let totalWorkHours = 0
        let pendingCount = 0

        for (const [userId, entries] of userMap) {
            entries.sort((a, b) => new Date(a.clock_in).getTime() - new Date(b.clock_in).getTime())

            const workHours = sumWorkHours(entries)

            totalWorkHours += workHours
            pendingCount += entries.filter(e => e.status === 'pending').length

            const overlappingEntryIds = detectOverlaps(entries)

            dayUserGroups.push({
                userId,
                userName: entries[0]?.expand?.user?.name || 'Unknown User',
                userColor: userColorMap?.get(userId),
                dayGroup: {
                    dateKey,
                    dateLabel: formatDateWithWeekday(dateKey),
                    entries,
                    totalWorkHours: workHours,
                    entryIds: entries.map(e => e.id),
                    hasOverlap: overlappingEntryIds.size > 0,
                    overlappingEntryIds,
                },
            })
        }

        dayUserGroups.sort((a, b) => a.userName.localeCompare(b.userName))

        sections.push({
            dateKey,
            dateLabel: formatDateWithWeekday(dateKey),
            totalWorkHours,
            pendingCount,
            data: dayUserGroups,
        })
    }

    sections.sort((a, b) => b.dateKey.localeCompare(a.dateKey))

    return sections
}
